/* Converts the static "New (EUR)" retail-reference prices (metafield
   custom.custom_price, always entered in euros) into the active presentment
   currency, using Shopify's own Markets conversion rate exposed on
   window.Shopify.currency.rate. Visitors browsing in EUR keep the
   server-rendered text untouched.

   PERF: the previous version installed a permanent document-wide
   MutationObserver for every visitor, including the EUR majority for whom
   convertAll() immediately returns. Weglot, Clarity, GTM and section
   rendering mutate the DOM all session long, so that observer never stopped
   running. It is now (a) never installed for EUR / unknown-currency visitors
   and (b) disconnected as soon as no unconverted node is left, then re-armed
   only around the moments this theme swaps markup in (section renders, filter
   updates, and the user interactions that open the cart drawer or load more
   products). Conversion output is unchanged. */
(function () {
  'use strict';

  var PENDING = '[data-hz-new-price]:not([data-hz-converted])';

  /* How long the observer stays armed after an interaction that may inject
     price nodes asynchronously (cart drawer fetch, quick add, load more). */
  var HOLD_MS = 3000;

  var observer = null;
  var observing = false;
  var releaseAfter = 0;
  var holdTimer = 0;

  /* Reads Shopify's Markets rate, or NaN when there is none.

     A missing, empty or null rate used to fall back to '1', which passed the
     check below and then printed the untouched euro amount with a foreign
     currency symbol in front of it - "New $249" for a price that only ever
     existed as EUR 249. No usable rate now means no conversion, so the
     server-rendered euro text stays as it is. */
  function currentRate() {
    var shopify = window.Shopify;
    if (!shopify || !shopify.currency) return NaN;
    var raw = shopify.currency.rate;
    if (raw === null || raw === undefined || raw === '') return NaN;
    return parseFloat(raw);
  }

  /* Only a non-EUR presentment currency with a usable rate needs any of this. */
  function needsConversion() {
    var shopify = window.Shopify;
    if (!shopify || !shopify.currency || !shopify.currency.active) return false;
    if (shopify.currency.active === 'EUR') return false;
    var rate = currentRate();
    return isFinite(rate) && rate > 0;
  }

  /* The metafield behind data-amount is free text, so the value can arrive as
     "1440", "1.440", "1,440" or "1440,00". parseFloat() on its own reads
     "1.440" as 1.44, which would advertise a reference price of one euro next
     to a product of fifteen hundred. Resolve the separators explicitly: a
     separator followed by exactly three digits groups thousands, anything else
     is the decimal mark. */
  function parseAmount(raw) {
    var s = String(raw === null || raw === undefined ? '' : raw).replace(/[^\d.,-]/g, '');
    if (!s) return NaN;

    var sep = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    if (sep === -1) return parseFloat(s);

    if (s.length - sep - 1 === 3) return parseFloat(s.replace(/[.,]/g, ''));

    return parseFloat(s.slice(0, sep).replace(/[.,]/g, '') + '.' + s.slice(sep + 1));
  }

  function arm(holdMs) {
    if (!needsConversion()) return;

    if (holdMs) {
      var until = Date.now() + holdMs;
      if (until > releaseAfter) releaseAfter = until;
    }

    if (!observing) {
      if (!observer) observer = new MutationObserver(schedule);
      observer.observe(document.documentElement, { childList: true, subtree: true });
      observing = true;
    }

    if (holdMs) {
      clearTimeout(holdTimer);
      // Final pass once the hold expires, which also releases the observer.
      holdTimer = setTimeout(convertAll, holdMs + 20);
    }
  }

  function release() {
    if (!observing) return;
    // Still parsing, or inside a hold window: more nodes may be on their way.
    if (document.readyState === 'loading') return;
    if (Date.now() < releaseAfter) return;
    if (document.querySelector(PENDING)) return;
    observer.disconnect();
    observing = false;
  }

  function convertAll() {
    if (!needsConversion()) return;
    var active = window.Shopify.currency.active;
    var rate = currentRate();

    var nodes = document.querySelectorAll(PENDING);
    if (!nodes.length) {
      release();
      return;
    }

    var formatter;
    try {
      formatter = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: active,
        currencyDisplay: 'narrowSymbol',
        useGrouping: false,
        maximumFractionDigits: 0
      });
    } catch (e) {
      return;
    }

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var base = parseAmount(el.getAttribute('data-amount'));
      if (!isFinite(base) || base <= 0) continue;
      el.textContent = 'New ' + formatter.format(Math.round(base * rate));
      el.setAttribute('data-hz-converted', '1');
    }

    release();
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      convertAll();
    }, 120);
  }

  /* Safe to call as often as we like: arm() is a no-op in EUR or while the
     observer already runs, and convertAll() skips converted nodes. */
  function run() {
    arm(0);
    convertAll();
  }

  function runAndHold() {
    arm(HOLD_MS);
    convertAll();
  }

  if (document.readyState === 'loading') {
    // Stay armed while the document streams in; release() waits for readiness.
    arm(0);
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-render signals this theme actually uses.
  document.addEventListener('shopify:section:load', runAndHold);
  ['filter:update', 'filter-update', 'FilterUpdate'].forEach(function (name) {
    document.addEventListener(name, runAndHold);
  });
  // Oneindig doorscrollen (Jens, 23 okt 2026): hv-infinite-scroll.js meldt nieuw geladen kaarten.
  document.addEventListener('hv:grid-append', runAndHold);

  /* Cart drawer, quick add and "load more" inject markup after a fetch without
     dispatching anything we can listen to, so a capture-phase click re-arms the
     observer for HOLD_MS. It costs one no-op call per click in EUR. */
  document.addEventListener(
    'click',
    function () {
      arm(HOLD_MS);
    },
    { capture: true, passive: true }
  );
})();
